/* eslint-disable react-hooks/exhaustive-deps */
import { Button } from '@efexteam/components';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Grid2,
  Typography,
  Box,
  useTheme,
  Divider,
} from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import GlobalLoader from '@efex-components/common/GlobalLoader/GlobalLoader';
import SnackBarComponent from '@efex-components/payables/addFundModal/components/CurrencySelector/SnackBarComponent';
import useWhiteLabelService from '@/layout/login/hooks/useWhiteLabel.service';
import LogoController from '@/layout/login/components/LogoController/LogoController';
import LangSelector from '@/components/navbar/lang';
import {
  useValidationState,
  useAccountValidation,
  useEmailResend,
} from './hooks';
import { useNavigation } from './hooks/useNavigation';
import { isRateLimitError } from './utils';
import getValidateAccountStyles from './VailidateAccount.style';

const ValidateAccount = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('uuid');

  const { userEmail, isEmailConfirmed } = useValidationState();
  const { isActivating, activationError, activateUser } = useAccountValidation({
    userId,
  });
  const {
    isResending,
    resendError,
    resendSuccess,
    canResend,
    timeUntilNextResend,
    resendEmail,
  } = useEmailResend({ userEmail });

  const theme = useTheme();
  const ValidateAccountStyles = getValidateAccountStyles(
    theme,
    isEmailConfirmed
  );

  const { whiteLabelState, getWhiteLabelFromLocalStorage } =
    useWhiteLabelService();
  const { isRedirecting, navigateToHome } = useNavigation();

  useEffect(() => {
    if (userId) {
      activateUser();
    }
  }, [userId, activateUser]);

  useEffect(() => {
    getWhiteLabelFromLocalStorage();
  }, [getWhiteLabelFromLocalStorage]);

  const handleClose = () => {
    navigateToHome();
  };

  const handleResendClick = () => {
    if (canResend) {
      resendEmail();
    }
  };

  const renderResendButton = () => {
    if (!canResend && timeUntilNextResend > 0) {
      return (
        <Button appearance="primary" disabled>
          {t('activateResend')} ({timeUntilNextResend}s)
        </Button>
      );
    }

    return (
      <Button
        appearance="primary"
        onClick={handleResendClick}
        disabled={!canResend || isResending}
      >
        {isResending ? t('sending') || 'Sending...' : t('activateResend')}
      </Button>
    );
  };

  const renderAlerts = () => {
    if (activationError) {
      return (
        <SnackBarComponent
          isOpen={!!activationError}
          message={
            activationError?.message ||
            t('activationError') ||
            'Activation failed'
          }
          severity="error"
          variant="filled"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      );
    }

    if (resendSuccess) {
      return (
        <SnackBarComponent
          isOpen={resendSuccess}
          message={t('activateResendSuccess')}
          variant="filled"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      );
    }

    if (resendError) {
      if (isRateLimitError(resendError)) {
        return (
          <Alert severity="error" className="my-5">
            {t('activateResendMaxTries')}
          </Alert>
        );
      }

      return (
        <Alert severity="error" className="my-5">
          {t('activateResendError')}
        </Alert>
      );
    }

    return null;
  };

  if (isRedirecting || isActivating) {
    return <GlobalLoader />;
  }

  if (!isEmailConfirmed) {
    return (
      <Box className="min-h-screen m-auto py-2">
        <div className="flex p-3">
          <LogoController
            isWhiteLabel={!!whiteLabelState?.organizationID}
            efexLogoProps={{ className: 'max-w-[130px] ml-3' }}
            whiteLabelLogo={whiteLabelState.organizationLogoURL}
          />
          <div className="flex items-center px-3 gap-5 ml-auto">
            <LangSelector />
            <Button
              size="medium"
              onClick={handleClose}
              appearance="secondary"
              aria-label={t('close') || 'Close'}
            >
              <CloseIcon />
            </Button>
          </div>
        </div>

        <Grid2
          container
          className="min-h-[calc(100vh-100px)]"
          justifyContent="center"
          alignItems="center"
        >
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box role="img" className={ValidateAccountStyles.iconContainer}>
              <AutoAwesomeIcon
                className={ValidateAccountStyles.awesomeIcon}
                aria-hidden="true"
              />
              <EmailOutlinedIcon aria-hidden="true" />
            </Box>

            <Typography variant="h4" className="text-center !mb-5">
              {t('activateViewTitle')}
            </Typography>

            <Typography variant="body2" className="!my-1 text-center">
              {t('activateBody', { email: userEmail })}
            </Typography>

            <Typography variant="body2" className="my-1 text-center">
              {t('activateSpam')}
            </Typography>

            <Divider className="!my-5 invisible" />

            {renderAlerts()}

            <Box className="flex items-center justify-center gap-8">
              <Button appearance="secondary" onClick={handleClose}>
                {t('activateGoBack')}
              </Button>
              {renderResendButton()}
            </Box>
          </Grid2>
        </Grid2>
      </Box>
    );
  }

  return (
    <Box className="min-h-screen m-auto py-2">
      <div className="flex p-3">
        <LogoController
          isWhiteLabel={!!whiteLabelState?.organizationID}
          efexLogoProps={{ className: 'max-w-[130px] ml-3' }}
          whiteLabelLogo={whiteLabelState.organizationLogoURL}
        />
        <div className="flex items-center px-3 gap-5 ml-auto">
          <LangSelector />
          <Button
            size="medium"
            onClick={handleClose}
            appearance="secondary"
            aria-label={t('close') || 'Close'}
          >
            <CloseIcon />
          </Button>
        </div>
      </div>

      <Grid2
        container
        className="min-h-[calc(100vh-100px)]"
        justifyContent="center"
        alignItems="center"
      >
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Box role="img" className={ValidateAccountStyles.iconContainer}>
            <AutoAwesomeIcon
              className={ValidateAccountStyles.awesomeIcon}
              aria-hidden="true"
            />
            <CheckCircleIcon aria-hidden="true" />
          </Box>

          <Typography variant="h4" className="!my-1 text-center">
            {userEmail}
          </Typography>

          <Typography variant="h4" className="text-center !mb-5">
            {t('activateSuccessMessage')}
          </Typography>

          <Divider className="!my-5 invisible" />

          <Box className="flex items-center justify-center gap-8">
            <Button appearance="secondary" onClick={handleClose}>
              {t('activateReturnDashboard')}
            </Button>
          </Box>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default ValidateAccount;
